/**
 * Student Portal — My Clinical Kit (contextual return workflow).
 *
 * Every component currently issued to the student is shown as a tile. Each
 * returnable tile carries a contextual [ RETURN ] action; consumables carry
 * a "not returnable" badge instead.
 *
 * Two entry points:
 *   • Single return  → click [ RETURN ] on a tile → focused modal.
 *   • Bulk return    → click [ RETURN COMPONENTS ] at the top → checkbox
 *                       mode → pick multiple items → one submission per
 *                       item using the SAME existing /api/returns endpoint.
 *
 * The dedicated Returns page (see pages/Returns.jsx) stays untouched — it is
 * the history / pending / broader-management surface. This page is the
 * contextual "return-what-I'm-holding-right-now" surface.
 *
 * Uses ONLY the existing endpoints:
 *   GET  /api/students/my-returnables
 *   GET  /api/orders           (for non-returnable consumables visibility)
 *   POST /api/returns          (unchanged contract, same 4 conditions the
 *                                Returns page already supports)
 *
 * NO database, NO schema, NO backend logic is changed.
 */
import React, { useMemo, useState } from 'react';
import { Package, RotateCcw, Calendar, Hash, X, Minus, Plus, Check, ShieldCheck } from 'lucide-react';
import { Card, ComponentMonogram, EmptyState, StatusPill, Modal } from '../ui';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const CONDITIONS = [
  { value: 'unused',    label: 'Unused',        hint: 'Sterile & untouched — can be re-issued after clerk verification.' },
  { value: 'used-good', label: 'Used · Good',   hint: 'Used but in good condition, suitable for cleaning & sterilisation.' },
  { value: 'damaged',   label: 'Damaged',       hint: 'Component is damaged or unfit for further clinical use.' },
  { value: 'lost',      label: 'Lost',          hint: 'Component cannot be located and cannot be returned physically.' },
];

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

export default function MyKitPage({ ctx }) {
  const { returnables, orders, refresh, showToast, openNewRequest } = ctx;

  /* ── Build the "kit" view ─────────────────────────────────
   * Combine returnables (which drive the returnable tiles) with any
   * non-returnable issued items (visible but not returnable). Each row is
   * uniquely keyed by (order_id, component_id).
   * ────────────────────────────────────────────────────────*/
  const kit = useMemo(() => {
    const rows = returnables
      .filter(r => r.return_status === 'returnable' || r.return_status === 'partially_returned' || r.return_status === 'pending_return')
      .map(r => ({
        key: `${r.order_id}-${r.component_id}`,
        order_id: r.order_id,
        order_code: r.order_code,
        component_id: r.component_id,
        component_name: r.component_name,
        brand_name: r.brand_name,
        size: r.size,
        qty_outstanding: r.qty_outstanding,
        qty_issued: r.qty_issued,
        qty_returned: r.qty_returned || 0,
        issued_at: r.issued_at,
        due: r.return_due_date,
        pending_return: r.return_status === 'pending_return',
        returnable: r.return_status !== 'pending_return',
      }));

    const seenKeys = new Set(rows.map(r => r.key));
    orders
      .filter(o => (o.status || '').toLowerCase() === 'issued')
      .forEach(o => {
        (o.items || []).forEach(it => {
          if (it.is_returnable) return;
          const key = `${o.id}-${it.component_id}`;
          if (seenKeys.has(key)) return;
          rows.push({
            key,
            order_id: o.id,
            order_code: `REQ-${o.id.slice(-8).toUpperCase()}`,
            component_id: it.component_id,
            component_name: it.component_name,
            brand_name: it.brand_name,
            size: it.size,
            qty_outstanding: it.quantity,
            qty_issued: it.quantity,
            qty_returned: 0,
            issued_at: o.issued_at || o.approved_at || o.created_at,
            due: null,
            pending_return: false,
            returnable: false,
          });
        });
      });

    return rows;
  }, [returnables, orders]);

  const returnableRows = useMemo(
    () => kit.filter(r => r.returnable && !r.pending_return),
    [kit]
  );

  // ── Return interaction state ─────────────────────────────
  const [singleReturn, setSingleReturn] = useState(null); // { row, qty, condition, notes, submitting }
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // { rows: [{row, qty, condition}], submitting, progress }

  const openSingleReturn = (row) => {
    setSingleReturn({
      row,
      qty: row.qty_outstanding,
      condition: 'used-good',
      notes: '',
      submitting: false,
    });
  };
  const closeSingleReturn = () => setSingleReturn(null);

  const bumpQty = (delta) => {
    if (!singleReturn) return;
    const next = singleReturn.qty + delta;
    if (next < 1 || next > singleReturn.row.qty_outstanding) return;
    setSingleReturn({ ...singleReturn, qty: next });
  };

  const submitSingle = async () => {
    if (!singleReturn) return;
    const { row, qty, condition, notes } = singleReturn;
    const q = Number(qty);
    if (!q || q < 1 || q > row.qty_outstanding) {
      showToast(`Quantity must be between 1 and ${row.qty_outstanding}.`, 'bad');
      return;
    }
    setSingleReturn({ ...singleReturn, submitting: true });
    try {
      await apiClient.post(endpoints.returns.create, {
        return_photos: [],
        notes: notes || `Student-initiated return · ${row.order_code}`,
        items: [{
          component_id: row.component_id,
          quantity: q,
          condition,
          source_order_id: row.order_id,
          component_name: row.component_name,
          brand_name: row.brand_name,
          size: row.size,
        }],
      });
      showToast('Return recorded. The clerk will verify it next.', 'ok');
      closeSingleReturn();
      await Promise.all([refresh.returns(), refresh.returnables(), refresh.orders()]);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not submit return.', 'bad');
      setSingleReturn(sr => sr ? { ...sr, submitting: false } : null);
    }
  };

  // ── Bulk selection ───────────────────────────────────────
  const toggleBulkMode = () => {
    setBulkMode(v => !v);
    setSelected(new Set());
  };
  const toggleSelected = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const openBulkModal = () => {
    if (selected.size === 0) return;
    const rows = returnableRows
      .filter(r => selected.has(r.key))
      .map(row => ({
        row,
        qty: row.qty_outstanding,
        condition: 'used-good',
      }));
    setBulkModal({ rows, submitting: false, progress: null });
  };
  const closeBulkModal = () => setBulkModal(null);

  const updateBulkRow = (key, patch) => {
    setBulkModal(m => m ? {
      ...m,
      rows: m.rows.map(r => r.row.key === key ? { ...r, ...patch } : r),
    } : m);
  };

  const submitBulk = async () => {
    if (!bulkModal) return;
    // Validate all
    for (const { row, qty } of bulkModal.rows) {
      const q = Number(qty);
      if (!q || q < 1 || q > row.qty_outstanding) {
        showToast(`Invalid quantity for ${row.component_name}. Must be 1–${row.qty_outstanding}.`, 'bad');
        return;
      }
    }
    setBulkModal(m => ({ ...m, submitting: true, progress: `0 / ${m.rows.length}` }));
    let success = 0;
    const failures = [];
    // One /api/returns POST per component (backend enforces its own duplicate
    // guard per source_order_id + component_id, giving us safe idempotency).
    for (let i = 0; i < bulkModal.rows.length; i++) {
      const { row, qty, condition } = bulkModal.rows[i];
      try {
        await apiClient.post(endpoints.returns.create, {
          return_photos: [],
          notes: `Bulk return · ${row.order_code}`,
          items: [{
            component_id: row.component_id,
            quantity: Number(qty),
            condition,
            source_order_id: row.order_id,
            component_name: row.component_name,
            brand_name: row.brand_name,
            size: row.size,
          }],
        });
        success += 1;
      } catch (err) {
        failures.push({ name: row.component_name, msg: err.response?.data?.detail || 'Failed' });
      }
      setBulkModal(m => m ? { ...m, progress: `${i + 1} / ${bulkModal.rows.length}` } : m);
    }
    await Promise.all([refresh.returns(), refresh.returnables(), refresh.orders()]);
    if (failures.length === 0) {
      showToast(`${success} return${success === 1 ? '' : 's'} recorded. Clerk will verify next.`, 'ok');
    } else if (success === 0) {
      showToast(`Bulk return failed. ${failures[0].msg}`, 'bad');
    } else {
      showToast(`${success} recorded, ${failures.length} failed. Check history for details.`, 'bad');
    }
    setBulkModal(null);
    setBulkMode(false);
    setSelected(new Set());
  };

  return (
    <>
      <div className="sp-flex-between" style={{ alignItems: 'start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <span className="sp-eyebrow">Portal</span>
          <h1 className="sp-page-title" data-testid="portal-mykit-title">My Clinical Kit</h1>
          <p className="sp-page-lede">
            Every component currently in your possession. Return items directly from a card, or select several to return together.
          </p>
        </div>
        {returnableRows.length > 0 && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {bulkMode ? (
              <>
                <span className="sp-subtle" data-testid="mykit-bulk-count">
                  {selected.size} selected
                </span>
                <button
                  type="button"
                  className="sp-btn sp-btn-ghost sp-btn-sm"
                  onClick={toggleBulkMode}
                  data-testid="mykit-bulk-cancel"
                >Cancel</button>
                <button
                  type="button"
                  className="sp-btn sp-btn-accent sp-btn-sm"
                  disabled={selected.size === 0}
                  onClick={openBulkModal}
                  data-testid="mykit-bulk-return-btn"
                >
                  <RotateCcw size={14} /> Return {selected.size || ''} selected
                </button>
              </>
            ) : (
              <button
                type="button"
                className="sp-btn sp-btn-ghost sp-btn-sm"
                onClick={toggleBulkMode}
                data-testid="mykit-bulk-toggle"
              >
                <RotateCcw size={14} /> Return components
              </button>
            )}
          </div>
        )}
      </div>

      <div className="sp-mt-6">
        {kit.length === 0 ? (
          <EmptyState
            icon={Package}
            title="Your kit is empty"
            message="You don't have any components currently issued to you. Submit a request to add components to your kit."
            action={(
              <button type="button" className="sp-btn sp-btn-primary" onClick={() => openNewRequest()} data-testid="mykit-new-request">
                Request a Component
              </button>
            )}
            testId="mykit-empty"
          />
        ) : (
          <div className="sp-tile-grid" data-testid="mykit-grid">
            {kit.map(row => {
              const isSelected = selected.has(row.key);
              const canSelect = bulkMode && row.returnable && !row.pending_return;
              const isOverdue = row.due && new Date(row.due).getTime() < Date.now()
                && row.returnable && !row.pending_return;
              const dueSoon = !isOverdue && row.due && row.returnable && !row.pending_return
                && (new Date(row.due).getTime() - Date.now()) < 3 * 24 * 60 * 60 * 1000;
              return (
                <article
                  key={row.key}
                  className={`sp-tile${canSelect ? ' is-clickable' : ''}${isSelected ? ' is-selected' : ''}`}
                  style={
                    isSelected
                      ? { borderColor: 'var(--diis-accent)', boxShadow: '0 0 0 2px var(--diis-accent-soft)' }
                      : isOverdue
                      ? { boxShadow: 'inset 3px 0 0 #B04A3C' }
                      : dueSoon
                      ? { boxShadow: 'inset 3px 0 0 var(--diis-warning)' }
                      : undefined
                  }
                  onClick={canSelect ? () => toggleSelected(row.key) : undefined}
                  data-testid={`kit-tile-${row.component_id}`}
                >
                  <ComponentMonogram brand={row.brand_name} name={row.component_name} />
                  <div className="sp-tile-body">
                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h4 className="sp-tile-name" title={row.component_name}>{row.component_name || 'Component'}</h4>
                        <div className="sp-tile-meta">
                          <span><strong>{row.brand_name || '—'}</strong></span>
                          {row.size && <span>{row.size}</span>}
                          <span className="sp-subtle"><Hash size={11} strokeWidth={1.7} />{row.order_code}</span>
                        </div>
                      </div>
                      {canSelect && (
                        <span
                          aria-hidden="true"
                          style={{
                            width: 20, height: 20,
                            borderRadius: 4,
                            border: `1.5px solid ${isSelected ? 'var(--diis-accent)' : 'var(--diis-hairline-strong)'}`,
                            background: isSelected ? 'var(--diis-accent)' : 'var(--diis-surface)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all var(--diis-motion-fast)',
                          }}
                          data-testid={`kit-select-${row.component_id}`}
                        >
                          {isSelected && <Check size={13} strokeWidth={3} color="#fff" />}
                        </span>
                      )}
                    </div>

                    <div className="sp-tile-meta">
                      <span><Calendar size={11} strokeWidth={1.7} />Issued {formatDate(row.issued_at)}</span>
                      <span>Qty <strong className="sp-mono-num">{row.qty_outstanding}</strong>
                        {row.qty_issued !== row.qty_outstanding && (
                          <span className="sp-subtle"> of {row.qty_issued}</span>
                        )}
                      </span>
                    </div>

                    <div className="sp-tile-footer">
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {isOverdue && (
                          <>
                            <StatusPill variant="overdue" data-testid={`kit-overdue-${row.component_id}`}>
                              Overdue since {formatDate(row.due)}
                            </StatusPill>
                            <span style={{ fontSize: 11, color: '#B04A3C', fontWeight: 600, letterSpacing: '0.04em' }}>
                              Return today
                            </span>
                          </>
                        )}
                        {dueSoon && !isOverdue && <StatusPill variant="warn">Due soon · {formatDate(row.due)}</StatusPill>}
                        {row.due && !isOverdue && !dueSoon && <StatusPill variant="neutral">Due {formatDate(row.due)}</StatusPill>}
                        {row.pending_return && <StatusPill variant="info">Return pending clerk</StatusPill>}
                        {!row.returnable && !row.pending_return && <StatusPill variant="neutral">Consumable · not returnable</StatusPill>}
                        {row.returnable && !row.pending_return && !row.due && <StatusPill variant="ok">In your kit</StatusPill>}
                      </div>
                      {row.returnable && !row.pending_return && !bulkMode && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-sm sp-btn-accent"
                          onClick={(e) => { e.stopPropagation(); openSingleReturn(row); }}
                          data-testid={`kit-return-btn-${row.component_id}`}
                        >
                          <RotateCcw size={13} /> Return
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Single return modal ────────────────────────────── */}
      <Modal
        open={!!singleReturn}
        onClose={closeSingleReturn}
        title="Return Component"
        subtitle={singleReturn ? `${singleReturn.row.component_name}${singleReturn.row.size ? ` · ${singleReturn.row.size}` : ''}` : ''}
        testId="mykit-single-return-modal"
        footer={singleReturn ? (
          <>
            <button
              type="button"
              className="sp-btn sp-btn-ghost"
              onClick={closeSingleReturn}
              disabled={singleReturn.submitting}
              data-testid="mykit-single-return-cancel"
            >Cancel</button>
            <button
              type="button"
              className="sp-btn sp-btn-accent"
              disabled={singleReturn.submitting}
              onClick={submitSingle}
              data-testid="mykit-single-return-submit"
            >{singleReturn.submitting ? 'Recording…' : 'Confirm Return'}</button>
          </>
        ) : null}
      >
        {singleReturn && (
          <>
            <div style={{ fontSize: 13, color: 'var(--diis-ink-muted)', marginBottom: 16 }}>
              You are returning up to <strong className="sp-mono-num" style={{ color: 'var(--diis-ink)' }}>{singleReturn.row.qty_outstanding}</strong> unit(s) from request <strong>{singleReturn.row.order_code}</strong>.
            </div>

            <div className="sp-grid-2">
              <div>
                <label className="sp-field-label">Quantity to return</label>
                <div className="sp-qty" data-testid="mykit-single-return-qty">
                  <button
                    type="button"
                    onClick={() => bumpQty(-1)}
                    disabled={singleReturn.qty <= 1}
                    aria-label="Decrease"
                  ><Minus size={14} /></button>
                  <input
                    type="number"
                    min={1}
                    max={singleReturn.row.qty_outstanding}
                    value={singleReturn.qty}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(singleReturn.row.qty_outstanding, Number(e.target.value) || 1));
                      setSingleReturn({ ...singleReturn, qty: n });
                    }}
                    data-testid="mykit-single-return-qty-input"
                  />
                  <button
                    type="button"
                    onClick={() => bumpQty(1)}
                    disabled={singleReturn.qty >= singleReturn.row.qty_outstanding}
                    aria-label="Increase"
                  ><Plus size={14} /></button>
                </div>
                <div className="sp-subtle sp-mt-2">Outstanding: {singleReturn.row.qty_outstanding}</div>
              </div>
              <div>
                <label className="sp-field-label">Condition on return</label>
                <select
                  className="sp-select"
                  value={singleReturn.condition}
                  onChange={(e) => setSingleReturn({ ...singleReturn, condition: e.target.value })}
                  data-testid="mykit-single-return-condition"
                >
                  {CONDITIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <div className="sp-subtle sp-mt-2">
                  {CONDITIONS.find(c => c.value === singleReturn.condition)?.hint}
                </div>
              </div>
            </div>

            <div className="sp-mt-4">
              <label className="sp-field-label">Notes (optional)</label>
              <textarea
                className="sp-textarea"
                value={singleReturn.notes}
                onChange={(e) => setSingleReturn({ ...singleReturn, notes: e.target.value })}
                placeholder="Anything the clerk should know about this return."
                data-testid="mykit-single-return-notes"
              />
            </div>
          </>
        )}
      </Modal>

      {/* ── Bulk return modal ──────────────────────────────── */}
      <Modal
        open={!!bulkModal}
        onClose={bulkModal?.submitting ? undefined : closeBulkModal}
        wide
        title={bulkModal ? `Return ${bulkModal.rows.length} component${bulkModal.rows.length === 1 ? '' : 's'}` : ''}
        subtitle="One clerk-verification request will be created per component. You cannot undo this."
        testId="mykit-bulk-return-modal"
        footer={bulkModal ? (
          <>
            <button
              type="button"
              className="sp-btn sp-btn-ghost"
              onClick={closeBulkModal}
              disabled={bulkModal.submitting}
              data-testid="mykit-bulk-return-cancel"
            >Cancel</button>
            <button
              type="button"
              className="sp-btn sp-btn-accent"
              disabled={bulkModal.submitting}
              onClick={submitBulk}
              data-testid="mykit-bulk-return-submit"
            >
              {bulkModal.submitting
                ? `Recording ${bulkModal.progress}…`
                : `Confirm All ${bulkModal.rows.length}`}
            </button>
          </>
        ) : null}
      >
        {bulkModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {bulkModal.rows.map(({ row, qty, condition }) => (
              <div
                key={row.key}
                style={{
                  padding: 14,
                  border: '1px solid var(--diis-hairline)',
                  borderRadius: 'var(--diis-radius)',
                  background: 'var(--diis-surface-sunk)',
                }}
                data-testid={`mykit-bulk-row-${row.component_id}`}
              >
                <div className="sp-flex-between" style={{ marginBottom: 10, alignItems: 'start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{row.component_name}</div>
                    <div className="sp-subtle" style={{ marginTop: 2 }}>{row.brand_name}{row.size ? ` · ${row.size}` : ''} · {row.order_code}</div>
                  </div>
                  <div className="sp-subtle sp-mono-num">Outstanding {row.qty_outstanding}</div>
                </div>
                <div className="sp-grid-2">
                  <div>
                    <label className="sp-field-label">Quantity</label>
                    <input
                      type="number"
                      className="sp-input"
                      min={1}
                      max={row.qty_outstanding}
                      value={qty}
                      onChange={(e) => {
                        const n = Math.max(1, Math.min(row.qty_outstanding, Number(e.target.value) || 1));
                        updateBulkRow(row.key, { qty: n });
                      }}
                      disabled={bulkModal.submitting}
                      data-testid={`mykit-bulk-qty-${row.component_id}`}
                    />
                  </div>
                  <div>
                    <label className="sp-field-label">Condition</label>
                    <select
                      className="sp-select"
                      value={condition}
                      onChange={(e) => updateBulkRow(row.key, { condition: e.target.value })}
                      disabled={bulkModal.submitting}
                      data-testid={`mykit-bulk-condition-${row.component_id}`}
                    >
                      {CONDITIONS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
